import { QueryClient, useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { StrategicRecommendation } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export type Data = StrategicRecommendation

export interface Variables {
  brandKitId: number
  recommendationId: number
  recommendation: string
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const updateRecommendation = async ({
  workspaceId,
  brandKitId,
  recommendationId,
  recommendation,
}: Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(
    `/w/${workspaceId}/brand_kits/${brandKitId}/strategic_recommendations/${recommendationId}`,
    {
      method: 'PUT',
      body: {
        user_prompt: recommendation,
      },
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const updateOptimistically = async ({
  queryClient,
  workspaceId,
  brandKitId,
  recommendationId,
  recommendation,
}: Variables & { queryClient: QueryClient; workspaceId: number }) => {
  const cacheKey = ['workspaces', workspaceId, 'brandKits', brandKitId, 'strategicRecommendations']
  await queryClient.cancelQueries(cacheKey)
  const previousRecommendations = queryClient.getQueryData(cacheKey) as StrategicRecommendation[] | undefined
  queryClient.setQueryData(cacheKey, (old: any) =>
    old?.map((oldRecommendation: StrategicRecommendation) => {
      if (oldRecommendation.id === recommendationId) {
        return { ...oldRecommendation, recommendation, reasoning: null, performance_metrics: null }
      }
      return oldRecommendation
    }),
  )
  return { previousRecommendations }
}

export const useUpdateRecommendation = (options?: Options) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, unknown, Variables>(
    'updateRecommendation',
    async (variables) => {
      return updateRecommendation({ workspaceId, ...variables })
    },
    {
      onMutate: async (variables) => {
        return updateOptimistically({ queryClient, workspaceId, ...variables })
      },
      ...options,
    },
  )
}
